import { Box, Container } from '@mui/material'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import { Outlet } from 'react-router-dom'

import { FeaturedAlertModal, PasswordChangeDialog, ThemeSelector } from '@/components'
import { useGetAlertsQuery, useReadAlertMutation } from '@/services/api'
import { accountSelector } from '@/store/accountSlice'

import DashboardHeader from './DashboardHeader'
import DashboardSidebar from './DashboardSidebar'

const SIDEBAR_WIDTH = 280
const SIDEBAR_COLLAPSED_WIDTH = 80
const SIDEBAR_COLLAPSED_STORAGE_KEY = 'sidebar-collapsed-state'

function Dashboard() {
  const [open, setOpen] = useState<boolean>(false)
  const { user, token } = useSelector(accountSelector)
  const { alerts, loadingAlerts } = useGetAlertsQuery(undefined, {
    selectFromResult: ({ data = [], isLoading, isFetching }) => ({
      alerts: data,
      loadingAlerts: isLoading || isFetching,
    }),
  })

  const [markAsRead, { isLoading: isMarkingRead }] = useReadAlertMutation()

  const activeFeaturedAlert = useMemo(() => {
    return alerts.find((alert) => alert.featured && !alert.read_at) || null
  }, [alerts])

  const handleMarkAsRead = async (alertId: number) => {
    await markAsRead(alertId).unwrap()
  }

  const [sidebarCollapsed, setSidebarCollapsed] = useState<boolean>(() => {
    const saved = localStorage.getItem(SIDEBAR_COLLAPSED_STORAGE_KEY)

    return saved !== null ? JSON.parse(saved) : false
  })

  useEffect(() => {
    localStorage.setItem(SIDEBAR_COLLAPSED_STORAGE_KEY, JSON.stringify(sidebarCollapsed))
  }, [sidebarCollapsed])

  const handleMobileClose = useCallback(() => setOpen(false), [])
  const handleMobileOpen = useCallback(() => setOpen((prev) => !prev), [])
  const handleSidebarToggle = useCallback(() => setSidebarCollapsed((prev) => !prev), [])

  const sidebarWidth = sidebarCollapsed ? SIDEBAR_COLLAPSED_WIDTH : SIDEBAR_WIDTH

  return (
    <Box
      data-qa="dashboard"
      sx={{
        backgroundColor: 'background.default',
        display: 'flex',
        height: '100%',
        overflow: 'hidden',
        width: '100%',
      }}>
      <ThemeSelector />

      <FeaturedAlertModal
        alert={activeFeaturedAlert}
        onMarkAsRead={handleMarkAsRead}
        isLoading={isMarkingRead}
      />

      {user && token && (
        <>
          <PasswordChangeDialog open={!user!.password_changed || false} user={user} />
          <DashboardHeader onMobileOpen={handleMobileOpen} />
          <DashboardSidebar
            open={open}
            onMobileClose={handleMobileClose}
            user={user}
            token={token}
            collapsed={sidebarCollapsed}
            onToggleCollapse={handleSidebarToggle}
            alerts={alerts}
          />
        </>
      )}
      <Box
        sx={{
          display: 'flex',
          flex: '1 1 auto',
          overflow: 'hidden',
          paddingTop: '4rem',
          pl: {
            lg: `${sidebarWidth}px`,
          },
          transition: (theme) =>
            theme.transitions.create(['padding-left'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.enteringScreen,
            }),
        }}>
        <Box
          sx={{
            display: 'flex',
            flex: '1 1 auto',
            overflow: 'hidden',
            backgroundColor: 'transparent',
          }}>
          <Box
            sx={{
              flex: '1 1 auto',
              height: '100%',
              overflow: 'auto',
              position: 'relative',
              WebkitOverflowScrolling: 'touch',
              '&::-webkit-scrollbar': {
                width: 8,
              },
              '&::-webkit-scrollbar-track': {
                backgroundColor: 'transparent',
              },
              '&::-webkit-scrollbar-thumb': {
                backgroundColor: 'action.disabled',
                borderRadius: 4,
                '&:hover': {
                  backgroundColor: 'action.hover',
                },
              },
            }}>
            <Box sx={{ minHeight: '100%', py: 4, px: 3 }}>
              <Container maxWidth={false} sx={{ px: { xs: 0, sm: 2 } }}>
                <Outlet context={{ alerts, loadingAlerts }} />
              </Container>
            </Box>
          </Box>
        </Box>
      </Box>
    </Box>
  )
}

export default Dashboard
